import Bold from "@tiptap/extension-bold";
import Document from "@tiptap/extension-document";
import FontSize from "@tiptap/extension-font-size";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import { useEffect, useRef } from "react";
import { useUnavailableVariableIds } from "@babackapp/variables-registry";
import createVariablesMentionExtension from "../../lib/mention-extension";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import { ParagraphBlock } from "./types";
import useThemeContextualColors from "../../hooks/use-theme-contextual-colors";
import { sectionToParent } from "../../hooks";


export function useParagraphEditor() {
  const blockProps = useBlockPanelPropsContext();
  const { formAPI, locale, defaultLocale, section, theme } = blockProps;
  const block = blockProps.block as ParagraphBlock;
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const unavailableVariableIds = useUnavailableVariableIds();

  const content =
    (block.content?.[locale] ?? block.content?.[defaultLocale])?.html ?? "";

  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const blockRef = useRef(block);
  const formAPIRef = useRef(formAPI);
  blockRef.current = block;
  formAPIRef.current = formAPI;

  const editor = useEditor({
    extensions: [
      Document,
      createVariablesMentionExtension(blockProps.variables, unavailableVariableIds),
      Paragraph.configure({
        HTMLAttributes: {
          style: "font-size:16px;font-weight:normal;margin:0;padding:0;",
        },
      }),
      Text,
      Bold,
      Italic,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "underline text-foreground",
        },
      }),
      TextStyle,
      Color,
      FontSize.configure({
        types: ["textStyle"],
      }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML();

      // Debounce the formAPI call to avoid excessive re-renders
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      updateTimeoutRef.current = setTimeout(() => {
        const updatedBlock = {
          ...blockRef.current,
          content: {
            ...blockRef.current.content,
            [locale]: { html: newContent },
          },
        };
        formAPIRef.current.updateField(
          formAPIRef.current.blockPath,
          updatedBlock,
        );
      }, 300);
    },
    editorProps: {
      attributes: {
        class:
          "min-h-[120px] max-h-[400px] overflow-auto px-3 py-2 text-sm rounded-md border bg-background outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-ring",
        style: `background-color: ${colors.bg}; color: ${colors.text}`,
      },
    },
  });

  useEffect(() => {
    if (editor && content) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) {
        editor.commands.setContent(content, {
          emitUpdate: false,
        });
      }
    }
  }, [editor, content, locale]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return editor;
}
